import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, Paper, Grid, Chip } from '@mui/material';
import { PlayArrow, Refresh, KeyboardArrowUp, KeyboardArrowDown, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { GlassContainer } from '../styles/sharedStyles';
import { useTranslation } from 'react-i18next';

const GRID_SIZE = 20;
const GAME_SPEED = 120;

const FOOD_ICONS = [
  '/immich.png', '/jellyfin.png', '/truenas_logo.png', '/home-assistant-alt.png',
  '/nextcloud.png', '/docker-moby.png', '/fastapi.png', '/reactjs.png',
  '/hdd.png', '/ssd.png', '/nvme.png', '/raspberry-pi.png',
  '/disk.png', '/ddns-updater.png', '/dockge.png', '/filebrowser.png',
  '/glances-light.png', '/jellystat.png', '/sqlitebrowser.png', '/wordpress.png'
];

const SnakeGame = () => {
  const { t } = useTranslation();
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [snake, setSnake] = useState([{ x: 10, y: 10 }]);
  const [food, setFood] = useState({ x: 15, y: 15, icon: FOOD_ICONS[0] });
  const [direction, setDirection] = useState('RIGHT');
  const [nextDirection, setNextDirection] = useState('RIGHT');
  const gameLoopRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const savedHighScore = localStorage.getItem('snakeGameHighScore');
    if (savedHighScore) {
      setHighScore(parseInt(savedHighScore, 10));
    }
  }, []);

  const getRandomPosition = useCallback(() => {
    return {
      x: Math.floor(Math.random() * GRID_SIZE),
      y: Math.floor(Math.random() * GRID_SIZE)
    };
  }, []);

  const getRandomIcon = useCallback(() => {
    return FOOD_ICONS[Math.floor(Math.random() * FOOD_ICONS.length)];
  }, []);

  const startGame = useCallback(() => {
    setSnake([{ x: 10, y: 10 }]);
    setFood({ ...getRandomPosition(), icon: getRandomIcon() });
    setDirection('RIGHT');
    setNextDirection('RIGHT');
    setScore(0);
    setGameOver(false);
    setGameStarted(true);
  }, [getRandomIcon, getRandomPosition]);

  const handleKeyDown = useCallback((e) => {
    if (!gameStarted || gameOver) return;

    const key = e.key.toLowerCase();
    
    // Map keys to directions
    const keyToDir = {
      'arrowup': 'UP', 'w': 'UP',
      'arrowdown': 'DOWN', 's': 'DOWN',
      'arrowleft': 'LEFT', 'a': 'LEFT',
      'arrowright': 'RIGHT', 'd': 'RIGHT'
    };

    const newDir = keyToDir[key];
    if (!newDir) return;

    // Prevent 180-degree turns
    const opposites = { 'UP': 'DOWN', 'DOWN': 'UP', 'LEFT': 'RIGHT', 'RIGHT': 'LEFT' };
    if (newDir !== opposites[direction]) {
      setNextDirection(newDir);
    }
  }, [gameStarted, gameOver, direction]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (!gameStarted || gameOver) return;

    const moveSnake = () => {
      setSnake(prevSnake => {
        const head = prevSnake[0];
        const newDir = direction;
        
        let newHead = { ...head };
        switch (newDir) {
          case 'UP': newHead.y -= 1; break;
          case 'DOWN': newHead.y += 1; break;
          case 'LEFT': newHead.x -= 1; break;
          case 'RIGHT': newHead.x += 1; break;
          default: break;
        }

        // Wall collision
        if (newHead.x < 0 || newHead.x >= GRID_SIZE || newHead.y < 0 || newHead.y >= GRID_SIZE) {
          setGameOver(true);
          if (score > highScore) {
            setHighScore(score);
            localStorage.setItem('snakeGameHighScore', score);
          }
          return prevSnake;
        }

        // Self collision
        if (prevSnake.some(segment => segment.x === newHead.x && segment.y === newHead.y)) {
          setGameOver(true);
          if (score > highScore) {
            setHighScore(score);
            localStorage.setItem('snakeGameHighScore', score);
          }
          return prevSnake;
        }

        const newSnake = [newHead, ...prevSnake];

        // Food collision
        if (newHead.x === food.x && newHead.y === food.y) {
          setScore(s => s + 10 + Math.floor(Math.random() * 5));
          setFood({ ...getRandomPosition(), icon: getRandomIcon() });
        } else {
          newSnake.pop();
        }

        return newSnake;
      });

      setDirection(nextDirection);
    };

    gameLoopRef.current = setInterval(moveSnake, GAME_SPEED);
    return () => clearInterval(gameLoopRef.current);
  }, [gameStarted, gameOver, direction, nextDirection, food, score, highScore, getRandomIcon, getRandomPosition]);

  const handleDirectionButton = (dir) => {
    if (!gameStarted || gameOver) return;
    
    const opposites = { 'UP': 'DOWN', 'DOWN': 'UP', 'LEFT': 'RIGHT', 'RIGHT': 'LEFT' };
    if (dir !== opposites[direction]) {
      setNextDirection(dir);
    }
  };

  // Focus handling for keyboard controls
  useEffect(() => {
    if (gameStarted && !gameOver && containerRef.current) {
      containerRef.current.focus();
    }
  }, [gameStarted, gameOver]);

  return (
    <Box ref={containerRef} tabIndex={0} sx={{ outline: 'none' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#00ff88' }}>
            🐍 Icon Snake
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
            Eat icons to grow! Use Arrow keys or WASD
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip label={`${t('games.snake.score')}: ${score}`} sx={{ bgcolor: 'rgba(0, 212, 255, 0.2)', color: '#00d4ff' }} />
          <Chip label={`${t('games.snake.highScore')}: ${highScore}`} sx={{ bgcolor: 'rgba(255, 215, 0, 0.2)', color: '#ffd700' }} />
          <Chip label={`Length: ${snake.length}`} sx={{ bgcolor: 'rgba(0, 255, 136, 0.2)', color: '#00ff88' }} />
        </Box>
      </Box>

      {!gameStarted ? (
        <GlassContainer sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" sx={{ mb: 2, color: '#00ff88' }}>
            🐍 Icon Snake
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', mb: 4, maxWidth: 500, mx: 'auto' }}>
            Guide the snake to eat all the NAS service icons! 
            The more you eat, the longer you grow.
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mb: 4 }}>
            {FOOD_ICONS.slice(0, 8).map((icon, i) => (
              <Box 
                key={i}
                component="img"
                src={icon}
                alt={`icon-${i}`}
                sx={{ width: 32, height: 32, objectFit: 'contain', filter: 'grayscale(50%)' }}
              />
            ))}
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', alignSelf: 'center' }}>
              +{FOOD_ICONS.length - 8} more...
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 4 }}>
            Use Arrow Keys or WASD to move
          </Typography>

          <Button 
            variant="contained" 
            size="large"
            startIcon={<PlayArrow />}
            onClick={startGame}
            sx={{ 
              bgcolor: 'linear-gradient(135deg, #00ff88 0%, #00cc6a 100%)',
              color: '#000',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              '&:hover': {
                bgcolor: 'linear-gradient(135deg, #00ff88 0%, #00ff88 100%)',
                boxShadow: '0 0 20px rgba(0, 255, 136, 0.5)'
              }
            }}
          >
            Start Snake
          </Button>
        </GlassContainer>
      ) : gameOver ? (
        <GlassContainer sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h3" sx={{ mb: 2, color: '#ff4444' }}>
            💀 {t('games.snake.gameOver')}
          </Typography>
          <Typography variant="h4" sx={{ mb: 4, color: '#00ff88' }}>
            {t('games.snake.score')}: {score}
          </Typography>
          <Typography variant="h5" sx={{ mb: 2, color: '#00d4ff' }}>
            Snake Length: {snake.length}
          </Typography>
          {score >= highScore && score > 0 && (
            <Typography variant="h5" sx={{ mb: 2, color: '#ffd700' }}>
              🏆 {t('games.snake.newHighScore')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button 
              variant="contained" 
              size="large"
              startIcon={<Refresh />}
              onClick={startGame}
              sx={{ 
                bgcolor: 'linear-gradient(135deg, #00ff88 0%, #00cc6a 100%)',
                color: '#000',
                fontWeight: 'bold',
                px: 4,
                '&:hover': {
                  boxShadow: '0 0 20px rgba(0, 255, 136, 0.5)'
                }
              }}
            >
              Play Again
            </Button>
          </Box>
        </GlassContainer>
      ) : (
        <GlassContainer sx={{ p: 3 }}>
          {/* Game Grid */}
          <Box 
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
              gap: 0,
              width: '100%',
              maxWidth: 500,
              mx: 'auto',
              aspectRatio: '1',
              bgcolor: 'rgba(0, 0, 0, 0.3)',
              borderRadius: 2,
              overflow: 'hidden',
              p: 1
            }}
          >
            {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, i) => {
              const x = i % GRID_SIZE;
              const y = Math.floor(i / GRID_SIZE);
              
              const isSnakeHead = snake[0].x === x && snake[0].y === y;
              const isSnakeBody = snake.some((segment, idx) => idx > 0 && segment.x === x && segment.y === y);
              const isFood = food.x === x && food.y === y;

              return (
                <Box
                  key={i}
                  sx={{
                    position: 'relative',
                    bgcolor: (x + y) % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {isSnakeHead && (
                    <Box sx={{ 
                      width: '80%', 
                      height: '80%', 
                      bgcolor: '#00ff88', 
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 10px rgba(0, 255, 136, 0.8)'
                    }}>
                      <Typography variant="h6" sx={{ color: '#000', fontWeight: 'bold' }}>
                        😎
                      </Typography>
                    </Box>
                  )}
                  {isSnakeBody && (
                    <Box sx={{ 
                      width: '70%', 
                      height: '70%', 
                      bgcolor: '#00cc6a', 
                      borderRadius: '30%',
                      opacity: 0.8
                    }} />
                  )}
                  {isFood && (
                    <Box 
                      component="img"
                      src={food.icon}
                      alt="food"
                      sx={{ 
                        width: '90%', 
                        height: '90%', 
                        objectFit: 'contain',
                        animation: 'pulse 0.5s ease-in-out infinite alternate'
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Mobile Controls */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 1 }}>
            <Grid container sx={{ maxWidth: 180 }}>
              <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={() => handleDirectionButton('UP')}
                  sx={{ 
                    minWidth: 50, 
                    height: 50,
                    bgcolor: 'rgba(0, 212, 255, 0.3)',
                    '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.5)' }
                  }}
                >
                  <KeyboardArrowUp />
                </Button>
              </Grid>
              <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={() => handleDirectionButton('LEFT')}
                  sx={{ 
                    minWidth: 50, 
                    height: 50,
                    bgcolor: 'rgba(0, 212, 255, 0.3)',
                    '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.5)' }
                  }}
                >
                  <KeyboardArrowLeft />
                </Button>
              </Grid>
              <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={() => handleDirectionButton('RIGHT')}
                  sx={{ 
                    minWidth: 50, 
                    height: 50,
                    bgcolor: 'rgba(0, 212, 255, 0.3)',
                    '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.5)' }
                  }}
                >
                  <KeyboardArrowRight />
                </Button>
              </Grid>
              <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={() => handleDirectionButton('DOWN')}
                  sx={{ 
                    minWidth: 50, 
                    height: 50,
                    bgcolor: 'rgba(0, 212, 255, 0.3)',
                    '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.5)' }
                  }}
                >
                  <KeyboardArrowDown />
                </Button>
              </Grid>
            </Grid>
          </Box>

          <style>{`
            @keyframes pulse {
              from { transform: scale(0.9); }
              to { transform: scale(1.1); }
            }
          `}</style>
        </GlassContainer>
      )}
    </Box>
  );
};

export default SnakeGame;
